import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Brain, 
  Zap, 
  TrendingUp, 
  Clock, 
  Target,
  ArrowRight,
  Crown,
  Lock,
  Calendar,
  Activity
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useDashboard } from '../contexts/DashboardContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import Header from '../components/Header.jsx';
import LoadingSpinner from '../components/LoadingSpinner.jsx';
import ErrorMessage from '../components/ErrorMessage.jsx';
import SubscriptionCard from '../components/SubscriptionCard.jsx';
import { colors, typography, spacing, radius, components, breakpoints, isMobile } from '../../styles/theme.js';
import { getFriendlyDate } from '../utils/dateUtils.js';
import { DIMENSIONS } from '../constants/gameConstants.js';

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { dashboardData, loading, error, fetchDashboard } = useDashboard();
  const { tier } = useSubscription();
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch dashboard data on mount
  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const mobile = windowWidth < breakpoints.mobile;
  const tablet = windowWidth >= breakpoints.mobile && windowWidth < breakpoints.desktop;

  if (loading && !dashboardData) {
    return (
      <>
        <Header />
        <div style={{
          minHeight: 'calc(100vh - 64px)',
          background: colors.bgPrimary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <LoadingSpinner message="Loading your training dashboard..." />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div style={{
          minHeight: 'calc(100vh - 64px)',
          background: colors.bgPrimary,
          padding: mobile ? spacing[4] : spacing[8]
        }}>
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <ErrorMessage error={error} onRetry={fetchDashboard} />
          </div>
        </div>
      </>
    );
  }

  const nbackData = dashboardData?.nback || {};
  const speedData = dashboardData?.speedTraining || {}; // Updated to match new API response
  const overallData = dashboardData?.overall || {};

  const getDimensionLabel = (comboCode) => {
    const codeToMapping = {
      'P': 'position', 'A': 'audio', 'V': 'voice', 'M': 'note',
      'C': 'color', 'S': 'shape', 'N': 'number', 'F': 'face',
      'R': 'arrow', 'O': 'movement', 'T': 'pattern', 'B': 'border',
      'L': 'orbitalPosition', 'Z': 'circleSize', 'X': 'circleRotation', 'I': 'circleBrightness',
      'W': 'categoryText', 'E': 'categoryAudio', 'Y': 'nameText', 'U': 'nameAudio', 
      'Q': 'math', 'H': 'rhythm', 'D': 'mandarinTone', 'G': 'volume'
    };
    
    const dimensions = comboCode.split('').map(code => codeToMapping[code] || code);
    return dimensions.map(dim => {
      const label = DIMENSIONS[dim]?.name || dim;
      return label.split(' ').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    }).join(' + ');
  };

  const isPro = tier === 'paid' || tier === 'pro';

  return (
    <>
      <Header />
      <div style={{
        minHeight: 'calc(100vh - 64px)',
        background: colors.bgPrimary,
        padding: mobile ? spacing[4] : spacing[8],
        fontFamily: typography.fontSans
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          {/* Welcome Section */}
          <div style={{ marginBottom: mobile ? spacing[6] : spacing[8] }}>
            <h1 style={{
              fontSize: mobile ? typography['3xl'] : typography['4xl'],
              fontWeight: typography.extrabold,
              marginBottom: spacing[2],
              color: colors.textPrimary,
              letterSpacing: '-0.02em',
              lineHeight: typography.tight
            }}>
              Welcome back{user?.email ? `, ${user.email.split('@')[0]}` : ''}!
            </h1>
            <p style={{
              color: colors.textTertiary,
              fontSize: mobile ? typography.sm : typography.base,
              margin: 0
            }}>
              Your cognitive training hub
            </p>
          </div>

          {/* Overall Stats Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: mobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: mobile ? spacing[3] : spacing[4],
            marginBottom: mobile ? spacing[6] : spacing[8]
          }}>
            {[
              { label: 'Training Days', value: nbackData.totalDays || 0, icon: Calendar, color: colors.primary },
              { label: 'Total Rounds', value: nbackData.totalRounds || 0, icon: Activity, color: colors.secondary },
              { label: 'Current Streak', value: overallData.currentStreak || 0, icon: TrendingUp, color: colors.accent },
              { label: 'Sessions', value: nbackData.totalSessions || 0, icon: Clock, color: colors.warning }
            ].map((stat, idx) => (
              <div key={idx} style={{
                ...components.card,
                padding: mobile ? spacing[4] : spacing[5],
                transition: 'transform 0.15s ease',
                cursor: 'default'
              }}>
                <stat.icon 
                  size={mobile ? 24 : 28} 
                  color={stat.color} 
                  style={{ marginBottom: spacing[2] }} 
                />
                <div style={{ 
                  fontSize: mobile ? typography['2xl'] : typography['3xl'], 
                  fontWeight: typography.bold, 
                  color: stat.color,
                  marginBottom: spacing[1],
                  lineHeight: typography.tight
                }}>
                  {stat.value}
                </div>
                <div style={{ 
                  fontSize: mobile ? typography.xs : typography.sm, 
                  color: colors.textTertiary,
                  fontWeight: typography.medium
                }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>

          {/* Subscription Card on Mobile (show at top) */}
          {mobile && (
            <div style={{ marginBottom: spacing[6] }}>
              <SubscriptionCard />
            </div>
          )}

          {/* Main Training Section */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: mobile ? '1fr' : tablet ? '1fr' : '1fr 320px',
            gap: mobile ? spacing[6] : spacing[8],
            marginBottom: mobile ? spacing[6] : spacing[8]
          }}>
            {/* Left Column: Training Cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: mobile ? spacing[4] : spacing[6] }}>
              {/* N-Back Training Card (Primary) */}
              <div
                onClick={() => navigate('/n-back')}
                style={{
                  ...components.card,
                  padding: mobile ? spacing[5] : spacing[6],
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  background: 'linear-gradient(135deg, rgba(96, 165, 250, 0.1) 0%, rgba(167, 139, 250, 0.1) 100%)',
                  border: `2px solid ${colors.primary}`
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 12px 24px rgba(96, 165, 250, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: spacing[4] }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: spacing[3] }}>
                    <Brain size={mobile ? 36 : 48} color={colors.primary} />
                    <div>
                      <h2 style={{
                        fontSize: mobile ? typography.xl : typography['2xl'],
                        fontWeight: typography.bold,
                        color: colors.primary,
                        marginBottom: spacing[1],
                        letterSpacing: '-0.01em'
                      }}>
                        N-Back Training
                      </h2>
                      <p style={{
                        fontSize: mobile ? typography.xs : typography.sm,
                        color: colors.textTertiary,
                        margin: 0
                      }}>
                        Your primary working memory trainer
                      </p>
                    </div>
                  </div>
                </div>

                {/* Active Combos */}
                {nbackData.activeCombos && nbackData.activeCombos.length > 0 && (
                  <div style={{ marginBottom: spacing[4] }}>
                    <div style={{
                      fontSize: mobile ? typography.xs : typography.sm,
                      color: colors.textMuted,
                      marginBottom: spacing[2],
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      fontWeight: typography.semibold
                    }}>
                      Active Dimensions
                    </div>
                    {nbackData.activeCombos.slice(0, 2).map((combo, idx) => (
                      <div key={idx} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: spacing[3],
                        background: colors.bgInput,
                        borderRadius: radius.md,
                        marginBottom: spacing[2]
                      }}>
                        <span style={{
                          fontSize: mobile ? typography.xs : typography.sm,
                          color: colors.textSecondary,
                          fontWeight: typography.medium
                        }}>
                          {getDimensionLabel(combo.comboCode)}
                        </span>
                        <span style={{
                          fontSize: mobile ? typography.sm : typography.base,
                          color: colors.primary,
                          fontWeight: typography.bold
                        }}>
                          Level {combo.currentLevel}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* CTA Button */}
                <button
                  style={{
                    width: '100%',
                    padding: mobile ? spacing[4] : spacing[5],
                    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                    border: 'none',
                    borderRadius: radius.lg,
                    color: 'white',
                    fontSize: mobile ? typography.base : typography.lg,
                    fontWeight: typography.bold,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: spacing[2],
                    fontFamily: typography.fontSans,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  Start Training
                  <ArrowRight size={mobile ? 20 : 24} />
                </button>
              </div>

              {/* Speed Training Card */}
              <div
                onClick={() => isPro ? navigate('/speed-training') : null}
                style={{
                  ...components.card,
                  padding: mobile ? spacing[5] : spacing[6],
                  cursor: isPro ? 'pointer' : 'default',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  opacity: isPro ? 1 : 0.7
                }}
                onMouseEnter={(e) => {
                  if (isPro) {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.15)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (isPro) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: spacing[4] }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: spacing[3] }}>
                    <Zap size={mobile ? 36 : 48} color={colors.warning} />
                    <div>
                      <h2 style={{
                        fontSize: mobile ? typography.xl : typography['2xl'],
                        fontWeight: typography.bold,
                        color: colors.warning,
                        marginBottom: spacing[1],
                        letterSpacing: '-0.01em'
                      }}>
                        Speed Training
                      </h2>
                      <p style={{
                        fontSize: mobile ? typography.xs : typography.sm,
                        color: colors.textTertiary,
                        margin: 0
                      }}>
                        Optimize neural processing speed
                      </p>
                    </div>
                  </div>
                  {!isPro && (
                    <Crown size={mobile ? 20 : 24} color={colors.warning} />
                  )}
                </div>

                {/* Speed Stats */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: spacing[3],
                  marginBottom: spacing[4]
                }}>
                  <div style={{
                    padding: spacing[3],
                    background: colors.bgInput,
                    borderRadius: radius.md
                  }}>
                    <div style={{
                      fontSize: mobile ? typography.xs : typography.sm,
                      color: colors.textMuted,
                      marginBottom: spacing[1]
                    }}>
                      Inspection Time
                    </div>
                    <div style={{
                      fontSize: mobile ? typography.lg : typography.xl,
                      fontWeight: typography.bold,
                      color: colors.primary
                    }}>
                      {speedData.inspection?.globalStats?.bestDuration ? `${speedData.inspection.globalStats.bestDuration}ms` : '—'}
                    </div>
                  </div>
                  <div style={{
                    padding: spacing[3],
                    background: colors.bgInput,
                    borderRadius: radius.md
                  }}>
                    <div style={{
                      fontSize: mobile ? typography.xs : typography.sm,
                      color: colors.textMuted,
                      marginBottom: spacing[1]
                    }}>
                      Reaction Time
                    </div>
                    <div style={{
                      fontSize: mobile ? typography.lg : typography.xl,
                      fontWeight: typography.bold,
                      color: colors.secondary
                    }}>
                      {speedData.reaction?.globalStats?.avgRT ? `${Math.round(speedData.reaction.globalStats.avgRT)}ms` : '—'}
                    </div>
                  </div>
                </div>

                {/* CTA or Lock Overlay */}
                {isPro ? (
                  <button
                    style={{
                      width: '100%',
                      padding: mobile ? spacing[4] : spacing[5],
                      background: colors.warning,
                      border: 'none',
                      borderRadius: radius.lg,
                      color: '#1f2937',
                      fontSize: mobile ? typography.base : typography.lg,
                      fontWeight: typography.bold,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: spacing[2],
                      fontFamily: typography.fontSans,
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.02)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    Train Speed
                    <ArrowRight size={mobile ? 20 : 24} />
                  </button>
                ) : (
                  <div style={{
                    width: '100%',
                    padding: mobile ? spacing[4] : spacing[5],
                    background: 'rgba(251, 191, 36, 0.1)',
                    border: `2px solid ${colors.warning}`,
                    borderRadius: radius.lg,
                    color: colors.warning,
                    fontSize: mobile ? typography.sm : typography.base,
                    fontWeight: typography.semibold,
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: spacing[2]
                  }}>
                    <Lock size={mobile ? 16 : 20} />
                    Pro Feature • Upgrade to Unlock
                  </div>
                )}
              </div>

              {/* Future Games Placeholder */}
              <div style={{
                ...components.card,
                padding: mobile ? spacing[5] : spacing[6],
                opacity: 0.5,
                cursor: 'not-allowed'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: spacing[3], marginBottom: spacing[3] }}>
                  <Target size={mobile ? 36 : 48} color={colors.textMuted} />
                  <div>
                    <h2 style={{
                      fontSize: mobile ? typography.xl : typography['2xl'],
                      fontWeight: typography.bold,
                      color: colors.textMuted,
                      marginBottom: spacing[1]
                    }}>
                      More Games Coming Soon
                    </h2>
                    <p style={{
                      fontSize: mobile ? typography.xs : typography.sm,
                      color: colors.textMuted,
                      margin: 0
                    }}>
                      Memory span, pattern recognition, and more
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Subscription Card (Desktop) */}
            {!mobile && !tablet && (
              <div style={{
                position: 'sticky',
                top: spacing[4],
                alignSelf: 'flex-start'
              }}>
                <SubscriptionCard />
              </div>
            )}
          </div>

          {/* Recent Activity Feed */}
          {nbackData.recentSessions && nbackData.recentSessions.length > 0 && (
            <div style={{
              ...components.card,
              padding: mobile ? spacing[5] : spacing[6]
            }}>
              <h2 style={{
                fontSize: mobile ? typography.xl : typography['2xl'],
                fontWeight: typography.bold,
                color: colors.textPrimary,
                marginBottom: spacing[5],
                letterSpacing: '-0.01em'
              }}>
                Recent Training Sessions
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[3] }}>
                {nbackData.recentSessions.map((session, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: mobile ? 'flex-start' : 'center',
                    flexDirection: mobile ? 'column' : 'row',
                    padding: spacing[4],
                    background: colors.bgInput,
                    borderRadius: radius.md,
                    gap: mobile ? spacing[3] : spacing[4]
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: mobile ? typography.sm : typography.base,
                        color: colors.textPrimary,
                        fontWeight: typography.semibold,
                        marginBottom: spacing[1]
                      }}>
                        {getDimensionLabel(session.comboCode)}
                      </div>
                      <div style={{
                        fontSize: mobile ? typography.xs : typography.sm,
                        color: colors.textTertiary
                      }}>
                        {getFriendlyDate(session.timestamp)}
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      gap: mobile ? spacing[4] : spacing[6],
                      alignItems: 'center'
                    }}>
                      <div style={{ textAlign: mobile ? 'left' : 'center' }}>
                        <div style={{
                          fontSize: mobile ? typography.xs : typography.sm,
                          color: colors.textMuted,
                          marginBottom: spacing[1]
                        }}>
                          Level
                        </div>
                        <div style={{
                          fontSize: mobile ? typography.lg : typography.xl,
                          fontWeight: typography.bold,
                          color: colors.primary
                        }}>
                          {session.nValue}
                        </div>
                      </div>
                      <div style={{ textAlign: mobile ? 'left' : 'center' }}>
                        <div style={{
                          fontSize: mobile ? typography.xs : typography.sm,
                          color: colors.textMuted,
                          marginBottom: spacing[1]
                        }}>
                          Accuracy
                        </div>
                        <div style={{
                          fontSize: mobile ? typography.lg : typography.xl,
                          fontWeight: typography.bold,
                          color: colors.accent
                        }}>
                          {session.overallAccuracy}%
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default DashboardPage;

